import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import type { FormEvent, PointerEvent as ReactPointerEvent } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { SendOutlined, CloseOutlined, DownOutlined } from "@ant-design/icons"
import { auth } from "firebase/firebase"
import { AGENT_URL, generateSessionId, streamAdkMessage } from "utils/adkChat"
import agentLogo from "assets/images/agent_d.png"
import "./styles.scss"

interface ChatMessage {
	id: string
	role: "user" | "assistant"
	text: string
}

let messageIdCounter = 0
const nextMessageId = () => `m-${++messageIdCounter}`

// Distance (px) from the bottom within which we still consider the user
// "at the bottom" — used for auto-scroll gating and the jump-to-bottom button.
const BOTTOM_THRESHOLD = 24

const MIN_W = 300
const MIN_H = 320

// FAB size (px) — kept in sync with `&__fab` in styles.scss so we can clamp the dragged position inside the viewport.
const FAB_SIZE = 48
// Viewport margin (px) the FAB is kept away from each edge while dragging.
const EDGE_MARGIN = 8
// Pointer travel (px) before a press is treated as a drag rather than a click.
const DRAG_THRESHOLD = 4

// --- Drag "feel" tuning ----------------------------------------------------
// How far (px) the ball is allowed to trail behind the cursor while dragging.
// Kept well under the ball's radius (~24px) so the cursor stays on the ball.
const MAX_LAG = 9
// Per-frame easing applied while dragging — lower = more trailing/rubber-band.
const DRAG_EASE = 0.28
// Spring constants used after release to settle the ball with a small
// brake-like overshoot.
const SPRING_STIFFNESS = 0.14
const SPRING_DAMPING = 0.3
// Below these thresholds (px and px/frame) the release spring is considered
// at rest and the animation loop stops.
const SETTLE_EPS = 0.4

const clamp = (n: number, lo: number, hi: number) =>
	Math.max(lo, Math.min(hi, n))

/**
 * Floating chat assistant ("Agent-D"). Mounted in the App layout so it appears
 * on every authenticated screen.
 *
 * Talks to the Google ADK server (see REACT_APP_AGENT_URL) via SSE — the
 * assistant bubble updates progressively as chunks arrive. A fresh ADK session
 * is generated on mount; closing/reopening the panel keeps it, reloading the
 * page starts a new one.
 *
 * Renders nothing when REACT_APP_AGENT_URL is unset (agent not deployed for
 * this environment).
 */
export function ChatWidget() {
	const [open, setOpen] = useState(false)
	const [messages, setMessages] = useState<ChatMessage[]>([])
	const [input, setInput] = useState("")
	const [sending, setSending] = useState(false)
	const [showJumpToBottom, setShowJumpToBottom] = useState(false)
	const [fabPos, setFabPos] = useState({ left: 24, bottom: 24 })
	const [panelSide, setPanelSide] = useState<"left" | "right">("right")
	const [panelVert, setPanelVert] = useState<"up" | "down">("up")

	const sectionRef = useRef<HTMLElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const scrollRef = useRef<HTMLDivElement>(null)
	const abortRef = useRef<AbortController | null>(null)
	// Mirrors `showJumpToBottom` so the messages-changed effect can read the
	// latest value without re-running each time the user scrolls.
	const atBottomRef = useRef(true)
	// True when we should refocus the input as soon as streaming ends.
	// Cleared if the user moves focus outside the widget while we're waiting.
	const wantsFocusRef = useRef(false)
	// Pointer-drag state for the top-left resize handle.
	const resizeStateRef = useRef<{
		startX: number
		startY: number
		startW: number
		startH: number
	} | null>(null)

	// Pointer-drag state for the floating action button.
	const dragStateRef = useRef<{
		startX: number
		startY: number
		moved: boolean
	} | null>(null)
	// Set when a drag just ended it doesn't also toggle the panel open/closed.
	const suppressClickRef = useRef(false)
	// Physics state for the floating button
	const posRef = useRef({ left: 24, bottom: 24 })
	const targetRef = useRef({ left: 24, bottom: 24 })
	const velRef = useRef({ left: 0, bottom: 0 })
	const draggingRef = useRef(false)
	const rafRef = useRef<number | null>(null)
	// Mirror `open`/`panelSide` so the stable physics loop can read the latest values while live-flipping the panel side during a drag.
	const openRef = useRef(open)
	const panelSideRef = useRef(panelSide)
	const panelVertRef = useRef(panelVert)
	openRef.current = open
	panelSideRef.current = panelSide
	panelVertRef.current = panelVert

	const sessionId = useMemo(generateSessionId, [])

	const scrollToBottom = useCallback(() => {
		const el = scrollRef.current
		if (!el) return
		el.scrollTop = el.scrollHeight
		atBottomRef.current = true
		setShowJumpToBottom(false)
	}, [])

	// Focus the input + reset scroll position whenever the panel opens.
	useEffect(() => {
		if (!open) return
		inputRef.current?.focus()
		const raf = requestAnimationFrame(scrollToBottom)
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false)
		}
		window.addEventListener("keydown", onKey)
		return () => {
			cancelAnimationFrame(raf)
			window.removeEventListener("keydown", onKey)
		}
	}, [open, scrollToBottom])

	// Auto-scroll when new content arrives, but only if the user is already
	// pinned to the bottom OR they just sent a message themselves.
	useEffect(() => {
		const el = scrollRef.current
		if (!el) return
		const last = messages[messages.length - 1]
		if (last?.role === "user" || atBottomRef.current) {
			el.scrollTop = el.scrollHeight
		}
	}, [messages, sending])

	// Watch for focus leaving the widget while we're waiting on a response.
	// If the user clicks somewhere else, we drop the "refocus when done" intent.
	useEffect(() => {
		if (!sending) return
		const onFocusIn = (e: FocusEvent) => {
			const target = e.target as Node | null
			if (
				target &&
				sectionRef.current &&
				!sectionRef.current.contains(target)
			) {
				wantsFocusRef.current = false
			}
		}
		document.addEventListener("focusin", onFocusIn)
		return () => document.removeEventListener("focusin", onFocusIn)
	}, [sending])

	// When streaming ends, refocus the input — deferred to the next frame so
	// React has re-rendered the input with `disabled={false}` first.
	useEffect(() => {
		if (sending || !wantsFocusRef.current) return
		wantsFocusRef.current = false
		const id = requestAnimationFrame(() => inputRef.current?.focus())
		return () => cancelAnimationFrame(id)
	}, [sending])

	useEffect(
		() => () => {
			abortRef.current?.abort()
			if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
		},
		[]
	)

	const onScroll = () => {
		const el = scrollRef.current
		if (!el) return
		const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
		const atBottom = distFromBottom < BOTTOM_THRESHOLD
		atBottomRef.current = atBottom
		setShowJumpToBottom(!atBottom && messages.length > 0)
	}

	const onResizePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
		const section = sectionRef.current
		if (!section) return
		const rect = section.getBoundingClientRect()
		resizeStateRef.current = {
			startX: e.clientX,
			startY: e.clientY,
			startW: rect.width,
			startH: rect.height,
		}
		e.currentTarget.setPointerCapture(e.pointerId)
		e.preventDefault()
	}

	const onResizePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
		const state = resizeStateRef.current
		const section = sectionRef.current
		if (!state || !section) return
		// The grip sits on the panel's outer corner (diagonally opposite the FAB).
		const dx = e.clientX - state.startX
		const dy = e.clientY - state.startY
		const widthDx = panelSide === "left" ? -dx : dx
		const heightDy = panelVert === "down" ? dy : -dy
		const newW = clamp(state.startW + widthDx, MIN_W, window.innerWidth - 48)
		const newH = clamp(state.startH + heightDy, MIN_H, window.innerHeight - 96)
		section.style.width = `${newW}px`
		section.style.height = `${newH}px`
	}

	const onResizePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
		if (!resizeStateRef.current) return
		resizeStateRef.current = null
		e.currentTarget.releasePointerCapture(e.pointerId)
	}

	// Single animation loop driving both the drag trail and the release spring.
	const runPhysics = useCallback(() => {
		const pos = posRef.current
		const target = targetRef.current
		const vel = velRef.current

		if (draggingRef.current) {
			// Ease toward the cursor so the ball trails behind a little…
			let nextLeft = pos.left + (target.left - pos.left) * DRAG_EASE
			let nextBottom = pos.bottom + (target.bottom - pos.bottom) * DRAG_EASE
			// …but never let it trail further than MAX_LAG, so the cursor stays
			// inside the ball.
			const gapL = target.left - nextLeft
			const gapB = target.bottom - nextBottom
			const gap = Math.hypot(gapL, gapB)
			if (gap > MAX_LAG) {
				const s = MAX_LAG / gap
				nextLeft = target.left - gapL * s
				nextBottom = target.bottom - gapB * s
			}
			// Track velocity so the release spring inherits the drag momentum.
			vel.left = nextLeft - pos.left
			vel.bottom = nextBottom - pos.bottom
			pos.left = nextLeft
			pos.bottom = nextBottom
			setFabPos({ left: pos.left, bottom: pos.bottom })
			// While the panel is open, live-flip its side/direction as the FAB
			// crosses the hysteresis bands so it always opens into the roomier half.
			if (openRef.current) {
				const centerX = pos.left + FAB_SIZE / 2
				const w = window.innerWidth
				if (panelSideRef.current === "right" && centerX > w * 0.5) {
					setPanelSide("left")
				} else if (panelSideRef.current === "left" && centerX < w * 0.5) {
					setPanelSide("right")
				}

				const centerY = pos.bottom + FAB_SIZE / 2
				const h = window.innerHeight
				if (panelVertRef.current === "up" && centerY > h * 0.5) {
					setPanelVert("down")
				} else if (panelVertRef.current === "down" && centerY < h * 0.5) {
					setPanelVert("up")
				}
			}
			rafRef.current = requestAnimationFrame(runPhysics)
			return
		}

		// Released: critically-ish damped spring back to the resting target,
		// carrying the drag momentum so it brakes with a small overshoot.
		vel.left =
			(vel.left + (target.left - pos.left) * SPRING_STIFFNESS) * SPRING_DAMPING
		vel.bottom =
			(vel.bottom + (target.bottom - pos.bottom) * SPRING_STIFFNESS) *
			SPRING_DAMPING
		pos.left += vel.left
		pos.bottom += vel.bottom

		const dist = Math.hypot(target.left - pos.left, target.bottom - pos.bottom)
		const speed = Math.hypot(vel.left, vel.bottom)
		if (dist < SETTLE_EPS && speed < SETTLE_EPS) {
			pos.left = target.left
			pos.bottom = target.bottom
			setFabPos({ left: pos.left, bottom: pos.bottom })
			rafRef.current = null
			return
		}
		setFabPos({ left: pos.left, bottom: pos.bottom })
		rafRef.current = requestAnimationFrame(runPhysics)
	}, [])

	const onFabPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
		dragStateRef.current = {
			startX: e.clientX,
			startY: e.clientY,
			moved: false,
		}
		draggingRef.current = true
		// Start from wherever the ball currently rests — no initial gap.
		targetRef.current = { ...posRef.current }
		velRef.current = { left: 0, bottom: 0 }
		e.currentTarget.setPointerCapture(e.pointerId)
		if (rafRef.current === null) {
			rafRef.current = requestAnimationFrame(runPhysics)
		}
	}

	const onFabPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
		const state = dragStateRef.current
		if (!state) return
		const dx = e.clientX - state.startX
		const dy = e.clientY - state.startY
		if (!state.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return
		state.moved = true
		const maxLeft = window.innerWidth - FAB_SIZE - EDGE_MARGIN
		const maxBottom = window.innerHeight - FAB_SIZE - EDGE_MARGIN
		// Target the ball's *center* on the cursor (ignoring where it was
		// grabbed), so pausing the cursor lets the ball recenter under it. The
		// animation loop eases the rendered position toward this target.
		targetRef.current = {
			left: clamp(e.clientX - FAB_SIZE / 2, EDGE_MARGIN, maxLeft),
			bottom: clamp(
				window.innerHeight - e.clientY - FAB_SIZE / 2,
				EDGE_MARGIN,
				maxBottom
			),
		}
	}

	const onFabPointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
		const state = dragStateRef.current
		if (!state) return
		dragStateRef.current = null
		draggingRef.current = false
		e.currentTarget.releasePointerCapture(e.pointerId)
		// A real drag should not also fire the open/close toggle.
		if (state.moved) suppressClickRef.current = true
		// Hand off to the release spring (loop is already running from drag).
		if (rafRef.current === null) {
			rafRef.current = requestAnimationFrame(runPhysics)
		}
	}

	const onFabClick = () => {
		if (suppressClickRef.current) {
			suppressClickRef.current = false
			return
		}
		// Opening: pick the side/direction with more room next to the FAB.
		if (!open) {
			const centerX = posRef.current.left + FAB_SIZE / 2
			const centerY = posRef.current.bottom + FAB_SIZE / 2
			setPanelSide(centerX > window.innerWidth / 2 ? "left" : "right")
			setPanelVert(centerY > window.innerHeight / 2 ? "down" : "up")
		}
		setOpen((v) => !v)
	}

	const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		const text = input.trim()
		if (!text || sending) return

		const userMsg: ChatMessage = { id: nextMessageId(), role: "user", text }
		const assistantId = nextMessageId()
		setMessages((m) => [
			...m,
			userMsg,
			{ id: assistantId, role: "assistant", text: "" },
		])
		setInput("")
		// Mark intent to refocus when streaming ends. The watcher effect will
		// clear this if the user moves focus outside the widget in the meantime.
		wantsFocusRef.current = true
		setSending(true)

		const controller = new AbortController()
		abortRef.current = controller

		try {
			for await (const accumulated of streamAdkMessage({
				userId: auth.currentUser?.uid ?? "anonymous",
				sessionId,
				message: text,
				signal: controller.signal,
			})) {
				setMessages((m) =>
					m.map((msg) =>
						msg.id === assistantId ? { ...msg, text: accumulated } : msg
					)
				)
			}
		} catch (err) {
			if ((err as Error).name === "AbortError") return
			setMessages((m) =>
				m.map((msg) =>
					msg.id === assistantId
						? {
								...msg,
								text: "Something went wrong reaching the assistant. Please try again.",
							}
						: msg
				)
			)
		} finally {
			setSending(false)
			abortRef.current = null
		}
	}

	// Agent not configured for this environment — render nothing.
	if (!AGENT_URL) return null

	return (
		<div
			className="chat-widget"
			style={{ left: fabPos.left, bottom: fabPos.bottom }}
		>
			{open ? (
				<section
					ref={sectionRef}
					role="dialog"
					aria-label="Chat assistant"
					className={`chat-widget__panel chat-widget__panel--${panelSide} chat-widget__panel--${panelVert}`}
				>
					<div
						role="separator"
						aria-label="Resize chat panel"
						onPointerDown={onResizePointerDown}
						onPointerMove={onResizePointerMove}
						onPointerUp={onResizePointerUp}
						onPointerCancel={onResizePointerUp}
						className="chat-widget__resize"
					>
						<svg
							width="12"
							height="12"
							viewBox="0 0 12 12"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							aria-hidden="true"
						>
							<path d="M2 6 L6 2" />
							<path d="M2 10 L10 2" />
						</svg>
					</div>

					<header className="chat-widget__header">
						<div className="chat-widget__title">
							<img
								src={agentLogo}
								alt=""
								className="chat-widget__avatar"
								draggable={false}
							/>
							<span>Assistant</span>
						</div>
						<button
							type="button"
							aria-label="Close chat panel"
							onClick={() => setOpen(false)}
							className="chat-widget__icon-btn"
						>
							<CloseOutlined />
						</button>
					</header>

					<div className="chat-widget__body">
						<div
							ref={scrollRef}
							onScroll={onScroll}
							className="chat-widget__scroll"
							aria-live="polite"
						>
							{messages.length === 0 && !sending ? (
								<p className="chat-widget__empty">
									Ask me anything about your operations.
								</p>
							) : null}
							{messages.map((m) => (
								<ChatBubble
									key={m.id}
									from={m.role}
									text={m.text}
									pending={
										sending &&
										m.role === "assistant" &&
										m.text === "" &&
										m.id === messages[messages.length - 1]?.id
									}
								/>
							))}
						</div>

						{showJumpToBottom ? (
							<button
								type="button"
								aria-label="Scroll to latest message"
								onClick={scrollToBottom}
								className="chat-widget__jump"
							>
								<DownOutlined />
							</button>
						) : null}
					</div>

					<form onSubmit={onSubmit} className="chat-widget__form">
						<label htmlFor="chat-input" className="chat-widget__sr-only">
							Message
						</label>
						<input
							id="chat-input"
							ref={inputRef}
							type="text"
							autoComplete="off"
							value={input}
							onChange={(e) => setInput(e.target.value)}
							placeholder="Type a message…"
							disabled={sending}
							className="chat-widget__input"
						/>
						<button
							type="submit"
							aria-label="Send message"
							disabled={sending || input.trim().length === 0}
							className="chat-widget__send"
						>
							<SendOutlined />
						</button>
					</form>
				</section>
			) : null}

			<button
				type="button"
				aria-label={open ? "Close chat" : "Open chat"}
				aria-expanded={open}
				onPointerDown={onFabPointerDown}
				onPointerMove={onFabPointerMove}
				onPointerUp={onFabPointerUp}
				onPointerCancel={onFabPointerUp}
				onClick={onFabClick}
				className="chat-widget__fab"
			>
				{open ? (
					<span className="chat-widget__fab-close">
						<CloseOutlined />
					</span>
				) : (
					<img
						src={agentLogo}
						alt="Open chat"
						className="chat-widget__fab-img"
						draggable={false}
					/>
				)}
			</button>
		</div>
	)
}

function ChatBubble({
	from,
	text,
	pending = false,
}: {
	from: ChatMessage["role"]
	text: string
	pending?: boolean
}) {
	const isUser = from === "user"
	return (
		<div
			className={`chat-widget__row chat-widget__row--${from}`}
			data-from={from}
		>
			<div
				className={`chat-widget__bubble chat-widget__bubble--${
					isUser ? "user" : "assistant"
				}${pending ? " chat-widget__bubble--pending" : ""}`}
			>
				{pending || isUser ? (
					pending ? (
						"…"
					) : (
						text
					)
				) : (
					<MarkdownContent text={text} />
				)}
			</div>
		</div>
	)
}

const MD_COMPONENTS = {
	a: ({ children, ...props }: any) => (
		<a
			className="chat-widget__md-link"
			target="_blank"
			rel="noreferrer"
			{...props}
		>
			{children}
		</a>
	),
}

const MarkdownContent = memo(function MarkdownContent({
	text,
}: {
	text: string
}) {
	return (
		<div className="chat-widget__md">
			<ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
				{text}
			</ReactMarkdown>
		</div>
	)
})

export default ChatWidget
